// ═══════════════════════════════════════════════════════════════
// InspectionWizard — step through a checklist one section at a
// time, marking each item and attaching photos and notes as you go.
//
// One section per screen rather than one ITEM per screen: the
// standard sheet runs ~30 items, and making a tech tap "Next" thirty
// times to walk a car is worse than a scrollable screen per area of
// the vehicle. Sections are short enough to thumb through while
// standing at the car.
//
// Everything lives in local state and is flushed to Firestore on a
// debounce, plus on every step change and on close — so a tech who
// loses signal mid-inspection keeps their work, and a tech who
// backgrounds the app doesn't come back to a half-saved checklist.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  X, ChevronLeft, ChevronRight, Camera, Loader2, Trash2, Check,
  ClipboardCheck, Gauge, MessageSquare, Plus, Lightbulb,
} from 'lucide-react';
import {
  INSPECTION_STATUSES, TIRE_CORNERS, TIRE_SUGGESTIONS, treadStatus,
  blankExtraItem,
} from './lib/inspection-template.js';
import { sectionProgress, inspectionProgress, statusMeta } from './lib/inspection-helpers.js';
import { compressImage, uploadFileWithProgress, removeFile } from './lib/storage.js';
import { shortId } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';
import InspectionPhotoReview from './InspectionPhotoReview.jsx';
import { inspectionPhotos, replaceInspectionPhoto } from './lib/inspection-photos.js';

const SAVE_DEBOUNCE_MS = 800;

// ─── Photo strip, shared by item rows and the tire grid ──────────
function PhotoStrip({ photos, uploading, uploadPct, onAdd, onRemove, onReview, label = 'Add photo' }) {
  return (
    <div className="insp-photo-row">
      {(photos || []).map((p) => (
        <div key={p.id} className="insp-photo-wrap">
          <button type="button" className="insp-photo-preview" onClick={() => onReview(p)} aria-label="Review inspection photo">
            <img src={p.url} alt="Inspection photo" className="insp-photo-thumb" />
          </button>
          <button
            type="button"
            className="insp-photo-remove"
            onClick={() => onRemove(p.id)}
            aria-label="Remove photo"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <label className={`insp-photo-add ${uploading ? 'busy' : ''}`}>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onAdd(f);
          }}
        />
        {uploading
          ? <><Loader2 size={14} className="spin" /> {uploadPct}%</>
          : <><Camera size={14} /> {label}</>}
      </label>
    </div>
  );
}

// ─── One checklist item ──────────────────────────────────────────
function ItemRow({
  item, onPatch, onAddPhoto, onRemovePhoto, onReviewPhoto, uploadKey, uploadPct,
  editableLabel = false, labelPlaceholder = '', onRemove = null, autoFocus = false,
}) {
  const meta = statusMeta(item.status);
  const uploading = uploadKey === item.key;
  // Notes are hidden until wanted — most items are just "OK" and a
  // textarea per row would bury the thing the tech actually taps.
  const [noteOpen, setNoteOpen] = useState(!!item.note);

  return (
    <li className={`insp-item ${meta.cls}`}>
      {editableLabel ? (
        <div className="insp-item-label-row">
          <input
            className="insp-item-label-input"
            value={item.label}
            maxLength={120}
            autoFocus={autoFocus}
            placeholder={labelPlaceholder}
            aria-label="What are you recommending?"
            onChange={(e) => onPatch({ label: e.target.value })}
          />
          {onRemove && (
            <button
              type="button"
              className="btn-link danger"
              onClick={onRemove}
              aria-label="Remove recommendation"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="insp-item-label">{item.label}</div>
      )}
      <div className="insp-status-row" role="group" aria-label={`${item.label} condition`}>
        {INSPECTION_STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={`insp-status-btn insp-${s.value}${item.status === s.value ? ' active' : ''}`}
            aria-pressed={item.status === s.value}
            onClick={() =>
              // Tapping the active status again clears it — the only
              // way back to "not checked" after a mis-tap.
              onPatch({ status: item.status === s.value ? null : s.value })
            }
          >
            {s.short}
          </button>
        ))}
      </div>

      <div className="insp-item-extras">
        {!noteOpen && (
          <button type="button" className="btn-link insp-note-toggle" onClick={() => setNoteOpen(true)}>
            <MessageSquare size={13} /> Add note
          </button>
        )}
        {noteOpen && (
          <textarea
            className="insp-note"
            rows={2}
            maxLength={500}
            placeholder="What did you see?"
            defaultValue={item.note || ''}
            onBlur={(e) => {
              const v = e.target.value;
              if (v !== (item.note || '')) onPatch({ note: v });
            }}
          />
        )}
        <PhotoStrip
          photos={item.photos}
          uploading={uploading}
          uploadPct={uploadPct}
          onAdd={(file) => onAddPhoto(file)}
          onRemove={onRemovePhoto}
          onReview={onReviewPhoto}
        />
      </div>
    </li>
  );
}

// ─── Tire grid ───────────────────────────────────────────────────
// Tread depth drives the colour automatically (7/32"+ green, 3–6
// yellow, 2 or less red) so the tech types one number instead of
// typing a number AND tapping a colour. The wear row stays manual —
// a tire can have plenty of tread and still be feathered or cupped.
function TireGrid({ grid, onPatch, onAddPhoto, onRemovePhoto, onReviewPhoto, uploadKey, uploadPct }) {
  const corners = grid.corners || {};

  function patchCorner(key, patch) {
    onPatch({ corners: { ...corners, [key]: { ...(corners[key] || {}), ...patch } } });
  }

  return (
    <div className="insp-tires">
      <p className="insp-tire-legend">
        <span className="insp-legend-chip insp-good">7/32&quot;+</span>
        <span className="insp-legend-chip insp-attention">3–6/32&quot;</span>
        <span className="insp-legend-chip insp-replace">2/32&quot; or less</span>
      </p>

      {TIRE_CORNERS.map(({ key, label, short }) => {
        const c = corners[key] || {};
        const auto = treadStatus(c.tread);
        const meta = statusMeta(c.wear || auto);
        return (
          <div key={key} className={`insp-tire-corner ${meta.cls}`}>
            <div className="insp-tire-corner-head">
              <span className="insp-tire-corner-name">
                <strong>{short}</strong> {label}
              </span>
              {auto && (
                <span className={`insp-status-badge ${statusMeta(auto).cls}`}>
                  {statusMeta(auto).label}
                </span>
              )}
            </div>

            <div className="insp-tire-fields">
              <label className="insp-tire-field">
                <span>Tread</span>
                <div className="insp-tire-input-wrap">
                  <input
                    type="number" inputMode="decimal" min="0" max="32" step="1"
                    value={c.tread ?? ''}
                    onChange={(e) => patchCorner(key, { tread: e.target.value })}
                    placeholder="—"
                  />
                  <span className="insp-tire-unit">/32&quot;</span>
                </div>
              </label>
              <label className="insp-tire-field">
                <span>PSI before</span>
                <input
                  type="number" inputMode="decimal" min="0" max="120"
                  value={c.pressureBefore ?? ''}
                  onChange={(e) => patchCorner(key, { pressureBefore: e.target.value })}
                  placeholder="—"
                />
              </label>
              <label className="insp-tire-field">
                <span>PSI after</span>
                <input
                  type="number" inputMode="decimal" min="0" max="120"
                  value={c.pressureAfter ?? ''}
                  onChange={(e) => patchCorner(key, { pressureAfter: e.target.value })}
                  placeholder="—"
                />
              </label>
            </div>

            <div className="insp-tire-wear">
              <span className="insp-tire-wear-label">Wear / damage</span>
              <div className="insp-status-row">
                {INSPECTION_STATUSES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    className={`insp-status-btn insp-${s.value}${c.wear === s.value ? ' active' : ''}`}
                    aria-pressed={c.wear === s.value}
                    onClick={() => patchCorner(key, { wear: c.wear === s.value ? null : s.value })}
                  >
                    {s.short}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <div className="insp-tire-block">
        <span className="insp-tire-wear-label">TPMS warning system</span>
        <div className="insp-status-row">
          {INSPECTION_STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              className={`insp-status-btn insp-${s.value}${grid.tpms === s.value ? ' active' : ''}`}
              aria-pressed={grid.tpms === s.value}
              onClick={() => onPatch({ tpms: grid.tpms === s.value ? null : s.value })}
            >
              {s.short}
            </button>
          ))}
        </div>
      </div>

      <div className="insp-tire-block">
        <span className="insp-tire-wear-label">Tire service suggested</span>
        <div className="insp-suggest-row">
          {TIRE_SUGGESTIONS.map(({ key, label }) => {
            const on = !!grid.suggestions?.[key];
            return (
              <button
                key={key}
                type="button"
                className={`insp-suggest-chip${on ? ' active' : ''}`}
                aria-pressed={on}
                onClick={() =>
                  onPatch({ suggestions: { ...(grid.suggestions || {}), [key]: !on } })
                }
              >
                {on && <Check size={12} strokeWidth={3} />} {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="insp-tire-block">
        <textarea
          className="insp-note"
          rows={2}
          maxLength={500}
          placeholder="Tire notes…"
          defaultValue={grid.note || ''}
          onBlur={(e) => {
            if (e.target.value !== (grid.note || '')) onPatch({ note: e.target.value });
          }}
        />
        <PhotoStrip
          photos={grid.photos}
          uploading={uploadKey === '__tires__'}
          uploadPct={uploadPct}
          onAdd={onAddPhoto}
          onRemove={onRemovePhoto}
          onReview={onReviewPhoto}
        />
      </div>
    </div>
  );
}

// ─── The wizard ──────────────────────────────────────────────────
export default function InspectionWizard({ jobId, inspection, vehicle, onSave, onClose }) {
  const [draft, setDraft] = useState(inspection);
  const [step, setStep] = useState(0);
  const [uploadKey, setUploadKey] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewKey, setReviewKey] = useState(null);
  // Focus the label of a just-added row so the tech can start typing.
  const [lastAddedExtra, setLastAddedExtra] = useState(null);

  // The saver reads from a ref so the debounce timer always flushes
  // the LATEST draft, not the one captured when the timer was armed.
  const draftRef = useRef(draft);
  const timerRef = useRef(null);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      setSaving(true);
      await onSave(draftRef.current);
      setError(null);
    } catch (e) {
      console.error('Inspection save failed:', e);
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [onSave]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flush(); }, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // Flush any pending edit when the wizard unmounts for any reason.
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      onSave(draftRef.current).catch((e) =>
        console.error('Final inspection save failed:', e)
      );
    }
  }, [onSave]);

  const sections = draft.sections || [];
  // Steps: [vehicle+mileage] + one per section + [comments/finish]
  const totalSteps = sections.length + 2;
  const isIntro  = step === 0;
  const isFinish = step === totalSteps - 1;
  const section  = !isIntro && !isFinish ? sections[step - 1] : null;

  const progress = useMemo(() => inspectionProgress(draft), [draft]);
  const extraItems = draft.extraItems || [];
  const photos = useMemo(() => inspectionPhotos(draft), [draft]);

  function reviewPhoto(photo) {
    if (uploadKey) return;
    const entry = photos.find((candidate) => candidate.photo === photo);
    if (entry) setReviewKey(entry.key);
  }

  async function replacePhoto(entry, replacement) {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const next = replaceInspectionPhoto(draftRef.current, entry, replacement);
    // Persist before changing the draft: failed retakes keep the original.
    await onSave(next);
    draftRef.current = next;
    setDraft(next);
  }

  function addExtra() {
    const item = blankExtraItem();
    setLastAddedExtra(item.key);
    mutate((prev) => ({ ...prev, extraItems: [...(prev.extraItems || []), item] }));
  }

  function patchExtra(key, patch) {
    mutate((prev) => ({
      ...prev,
      extraItems: (prev.extraItems || []).map((i) => (i.key === key ? { ...i, ...patch } : i)),
    }));
  }

  function removeExtra(key) {
    mutate((prev) => ({
      ...prev,
      extraItems: (prev.extraItems || []).filter((i) => i.key !== key),
    }));
  }

  function mutate(fn) {
    setDraft((prev) => {
      const next = fn(prev);
      draftRef.current = next;
      return next;
    });
    scheduleSave();
  }

  function patchSection(sectionKey, patch) {
    mutate((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.key === sectionKey ? { ...s, ...patch } : s)),
    }));
  }

  function patchItem(sectionKey, itemKey, patch) {
    mutate((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.key !== sectionKey
          ? s
          : { ...s, items: s.items.map((i) => (i.key === itemKey ? { ...i, ...patch } : i)) }
      ),
    }));
  }

  function patchTires(sectionKey, patch) {
    mutate((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.key !== sectionKey ? s : { ...s, tires: { ...(s.tires || {}), ...patch } }
      ),
    }));
  }

  // Removing a photo has to delete the FILE, not just the reference.
  // Dropping it from the array alone left the image in Storage
  // forever: invisible to everyone and still billed every month.
  //
  // The file goes first and failure is swallowed — if Storage is
  // unreachable, the tech should still see the photo disappear rather
  // than be stuck with a thumbnail they can't remove. A stray file is
  // a smaller problem than an unusable screen.
  async function deletePhotoFile(photo) {
    if (!photo?.path) return;
    try {
      await removeFile(photo.path);
    } catch (e) {
      console.warn('Could not delete inspection photo from storage:', e);
    }
  }

  // Photos upload immediately — the file is in hand and the tech may
  // walk out of signal before the debounce fires.
  async function uploadPhoto(file, storageKey, apply) {
    setUploadKey(storageKey);
    setUploadPct(0);
    setError(null);
    try {
      const blob = await compressImage(file);
      const photoId = shortId();
      const path = `inspections/${jobId}/${storageKey}/${photoId}.jpg`;
      const url = await uploadFileWithProgress(
        path, blob, { contentType: 'image/jpeg' },
        (p) => setUploadPct(Math.round(p * 100))
      );
      apply({ id: photoId, url, path, createdAt: Date.now() });
      await flush();
    } catch (e) {
      console.error('Photo upload failed:', e);
      setError(e.message);
    } finally {
      setUploadKey(null);
      setUploadPct(0);
    }
  }

  async function goToStep(next) {
    await flush();
    setStep(next);
    // A new screen should start at the top, not wherever the last
    // one was scrolled to.
    document.querySelector('.insp-wizard-body')?.scrollTo({ top: 0 });
  }

  async function handleClose() {
    await flush();
    onClose();
  }

  const headerVehicle = vehicle ? vehicleLabel(vehicle) : null;

  return (
    <div className="modal-overlay insp-wizard-overlay">
      <div className="insp-wizard" role="dialog" aria-modal="true" aria-label="Vehicle inspection">
        <header className="insp-wizard-head">
          <div className="insp-wizard-head-text">
            <h2>{draft.templateName || 'Vehicle inspection'}</h2>
            <p className="muted small">
              {headerVehicle ? `${headerVehicle} · ` : ''}
              {progress.checked}/{progress.total} checked
            </p>
          </div>
          <button type="button" className="btn-link" onClick={handleClose} aria-label="Close inspection">
            <X size={20} />
          </button>
        </header>
        {photos.length > 0 && (
          <button type="button" className="btn-secondary insp-review-photos" disabled={!!uploadKey}
            onClick={() => setReviewKey(photos[0].key)}>
            <Camera size={15} /> Review photos ({photos.length})
          </button>
        )}

        <div className="insp-progress-track" aria-hidden="true">
          <div
            className="insp-progress-fill"
            style={{ width: `${progress.total ? (progress.checked / progress.total) * 100 : 0}%` }}
          />
        </div>

        {/* Jump straight to any section — a tech who only wants to
            re-check the brakes shouldn't have to page through. */}
        <nav className="insp-step-strip" aria-label="Inspection sections">
          <button
            type="button"
            className={`insp-step-chip${isIntro ? ' active' : ''}`}
            onClick={() => goToStep(0)}
          >
            Vehicle
          </button>
          {sections.map((s, i) => {
            const p = sectionProgress(s);
            const done = p.total > 0 && p.checked === p.total;
            return (
              <button
                key={s.key}
                type="button"
                className={`insp-step-chip${step === i + 1 ? ' active' : ''}${done ? ' done' : ''}`}
                onClick={() => goToStep(i + 1)}
              >
                {done && <Check size={11} strokeWidth={3} />} {s.label}
              </button>
            );
          })}
          <button
            type="button"
            className={`insp-step-chip${isFinish ? ' active' : ''}`}
            onClick={() => goToStep(totalSteps - 1)}
          >
            Finish
          </button>
        </nav>

        <div className="insp-wizard-body">
          {error && <p className="form-error">{error}</p>}

          {isIntro && (
            <section className="insp-step">
              <h3 className="insp-step-title"><Gauge size={16} /> Vehicle &amp; mileage</h3>
              <p className="muted small">
                {headerVehicle
                  ? <>Inspecting <strong>{headerVehicle}</strong>.</>
                  : <>No vehicle is attached to this job — the inspection will still save.</>}
              </p>
              {vehicle?.vin && (
                <p className="muted small insp-vin">VIN {vehicle.vin}</p>
              )}
              {vehicle?.plate && (
                <p className="muted small insp-vin">Plate {vehicle.plate}</p>
              )}
              <label className="insp-odo-field">
                <span>Odometer</span>
                <div className="insp-tire-input-wrap">
                  <input
                    type="number" inputMode="numeric" min="0"
                    value={draft.odometer ?? ''}
                    onChange={(e) => mutate((prev) => ({ ...prev, odometer: e.target.value }))}
                    placeholder={vehicle?.odometer ? String(vehicle.odometer) : 'Miles'}
                  />
                  <span className="insp-tire-unit">mi</span>
                </div>
              </label>
              <p className="muted small">
                Work through each section, then finish up with any overall comments.
              </p>
            </section>
          )}

          {section && (
            <section className="insp-step">
              <h3 className="insp-step-title">
                <ClipboardCheck size={16} /> {section.label}
              </h3>
              {section.kind === 'tires' ? (
                <TireGrid
                  onReviewPhoto={reviewPhoto}
                  grid={section.tires || {}}
                  onPatch={(patch) => patchTires(section.key, patch)}
                  uploadKey={uploadKey}
                  uploadPct={uploadPct}
                  onAddPhoto={(file) =>
                    uploadPhoto(file, '__tires__', (photo) =>
                      patchTires(section.key, {
                        photos: [...(section.tires?.photos || []), photo],
                      })
                    )
                  }
                  onRemovePhoto={(photoId) => {
                    const photos = section.tires?.photos || [];
                    deletePhotoFile(photos.find((p) => p.id === photoId));
                    patchTires(section.key, {
                      photos: photos.filter((p) => p.id !== photoId),
                    });
                  }}
                />
              ) : (
                <ul className="insp-item-list">
                  {(section.items || []).map((item) => (
                      <ItemRow
                        onReviewPhoto={reviewPhoto}
                      key={item.key}
                      item={item}
                      uploadKey={uploadKey}
                      uploadPct={uploadPct}
                      onPatch={(patch) => patchItem(section.key, item.key, patch)}
                      onAddPhoto={(file) =>
                        uploadPhoto(file, `${section.key}/${item.key}`, (photo) =>
                          patchItem(section.key, item.key, {
                            photos: [...(item.photos || []), photo],
                          })
                        )
                      }
                      onRemovePhoto={(photoId) => {
                        deletePhotoFile((item.photos || []).find((p) => p.id === photoId));
                        patchItem(section.key, item.key, {
                          photos: (item.photos || []).filter((p) => p.id !== photoId),
                        });
                      }}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}

          {isFinish && (
            <section className="insp-step">
              {/* Anything the checklist doesn't cover — the noise the
                  customer mentioned, a part this template has no row
                  for. These become findings like any other, so they
                  can be quoted and sent with the estimate. */}
              <h3 className="insp-step-title">
                <Lightbulb size={16} /> Also recommend
              </h3>
              <p className="muted small" style={{ marginTop: -6, marginBottom: 10 }}>
                Something you want to flag that isn't on the checklist.
              </p>

              {extraItems.length > 0 && (
                <ul className="insp-item-list" style={{ marginBottom: 10 }}>
                  {extraItems.map((item, i) => (
                      <ItemRow
                        onReviewPhoto={reviewPhoto}
                      key={item.key}
                      item={item}
                      editableLabel
                      autoFocus={item.key === lastAddedExtra && i === extraItems.length - 1}
                      labelPlaceholder="e.g. Rear wiper blade is torn"
                      uploadKey={uploadKey}
                      uploadPct={uploadPct}
                      onPatch={(patch) => patchExtra(item.key, patch)}
                      onRemove={() => removeExtra(item.key)}
                      onAddPhoto={(file) =>
                        uploadPhoto(file, `extra/${item.key}`, (photo) =>
                          patchExtra(item.key, { photos: [...(item.photos || []), photo] })
                        )
                      }
                      onRemovePhoto={(photoId) => {
                        deletePhotoFile((item.photos || []).find((p) => p.id === photoId));
                        patchExtra(item.key, {
                          photos: (item.photos || []).filter((p) => p.id !== photoId),
                        });
                      }}
                    />
                  ))}
                </ul>
              )}

              <button type="button" className="btn-secondary" onClick={addExtra}>
                <Plus size={15} /> Add a recommendation
              </button>

              <h3 className="insp-step-title" style={{ marginTop: 24 }}>
                <MessageSquare size={16} /> Comments
              </h3>
              <textarea
                className="insp-note insp-comments"
                rows={4}
                maxLength={2000}
                placeholder="Anything the customer should know overall…"
                defaultValue={draft.comments || ''}
                onBlur={(e) => mutate((prev) => ({ ...prev, comments: e.target.value }))}
              />

              <h3 className="insp-step-title" style={{ marginTop: 20 }}>Summary</h3>
              <ul className="insp-summary-list">
                {sections.map((s) => {
                  const p = sectionProgress(s);
                  const complete = p.total > 0 && p.checked === p.total;
                  return (
                    <li key={s.key} className="insp-summary-row">
                      <span>{s.label}</span>
                      <span className={`insp-summary-count${complete ? ' done' : ''}`}>
                        {p.checked}/{p.total}
                        {complete && <Check size={12} strokeWidth={3} />}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {progress.checked < progress.total && (
                <p className="muted small" style={{ marginTop: 10 }}>
                  {progress.total - progress.checked} item
                  {progress.total - progress.checked === 1 ? '' : 's'} still unchecked. You can
                  finish anyway — only what you marked shows on the customer's report.
                </p>
              )}
            </section>
          )}
        </div>

        <footer className="insp-wizard-foot">
          <button
            type="button"
            className="btn-secondary"
            disabled={isIntro}
            onClick={() => goToStep(Math.max(0, step - 1))}
          >
            <ChevronLeft size={16} /> Back
          </button>
          <span className="muted small insp-save-state">
            {saving ? 'Saving…' : 'Saved'}
          </span>
          {isFinish ? (
            <button type="button" className="btn" onClick={handleClose}>
              <Check size={16} strokeWidth={2.5} /> Done
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() => goToStep(Math.min(totalSteps - 1, step + 1))}
            >
              Next <ChevronRight size={16} />
            </button>
          )}
        </footer>
      </div>
      {reviewKey && photos.length > 0 && (
        <InspectionPhotoReview photos={photos} initialKey={reviewKey} jobId={jobId}
          onClose={() => setReviewKey(null)} onReplace={replacePhoto} />
      )}
    </div>
  );
}
